import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { logRealtimeStatus } from '@/utils/realtimeStatusLog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { ImportReportData } from '@/components/tickets/ImportReport';
import { NeedsConfirmationQueue } from '@/components/optiqueue/NeedsConfirmationQueue';

export interface Notification {
  id: string;
  type: 'import' | 'reassign' | 'error' | 'info' | 'warning' | 'success' | 'rollover' | 'timer';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  data?: any;
  targetRoles?: any[];
  targetTechId?: string;
}

interface NotificationPanelProps {
  notifications: Notification[];
  onClearAll?: () => void;
  onMarkAsRead?: (id: string) => void;
  onViewImportReport?: (report: ImportReportData) => void;
}

export const NotificationPanel = ({ 
  notifications: externalNotifications, 
  onClearAll: externalClearAll,
  onMarkAsRead: externalMarkAsRead,
  onViewImportReport 
}: NotificationPanelProps) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const notificationContext = useNotifications();
  const { membership, user } = useAuth();
  
  // Filter notifications based on user role
  const userRole = membership?.role;
  
  // Get technician ID if user is a technician (need to fetch from technician_users table)
  const [technicianId, setTechnicianId] = useState<string | null>(null);
  
  // ARC-A3: deps tightened from `[user, membership, userRole]` (full
  // objects) to `[user?.id, membership?.shop_id, userRole]`. The
  // effect reads `user.id`, `membership.shop_id`, and `userRole` (a
  // primitive derived from `membership?.role`); tightening removes
  // re-fires triggered by user/membership reference replacement that
  // doesn't change either id field.
  useEffect(() => {
    const fetchTechnicianId = async () => {
      if (user && membership?.shop_id && userRole === 'TECHNICIAN') {
        const { data } = await supabase
          .from('technician_users')
          .select('technician_id')
          .eq('user_id', user.id)
          .eq('shop_id', membership.shop_id)
          .maybeSingle();

        if (data) setTechnicianId(data.technician_id);
      }
    };
    fetchTechnicianId();
  }, [user?.id, membership?.shop_id, userRole]);
  
  // Load notifications from database. Hoisted into a `useCallback`
  // (slice 4D.1 follow-up) so we can re-invoke it when the bell sheet
  // opens — without this, `loadNotifications` only ran on initial mount,
  // and persistent rows inserted after sign-in (e.g. slice-4D custody
  // notifications) never appeared in the panel until the user signed
  // out and back in (because realtime is currently flaky per the
  // ongoing investigation).
  const loadNotifications = useCallback(async () => {
    if (!user || !membership?.shop_id) return;

    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('shop_id', membership.shop_id)
      .order('created_at', { ascending: false })
      .limit(100);

    if (!data) return;

    // Clear existing notifications first to avoid duplicates
    notificationContext.clearAll();

    const dbNotifications: Notification[] = data
      .filter(n => {
        // Filter out notifications deleted by this user
        const deletedBy = n.deleted_by || [];
        return !deletedBy.includes(user.id);
      })
      .map(n => ({
        id: n.id,
        type: n.type as any,
        title: n.title,
        message: n.message,
        timestamp: new Date(n.created_at),
        read: n.read_by?.includes(user.id) || false,
        data: n.data,
        targetRoles: n.target_roles as any[],
        targetTechId: n.target_tech_id || undefined,
      }));

    // Add notifications with their original IDs (not generating new ones)
    dbNotifications.forEach(n => {
      notificationContext.addNotification(n);
    });
    // notificationContext methods are stable singletons; intentionally
    // omitted from deps to avoid refetch storms.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, membership?.shop_id]);

  // Initial load on mount.
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Slice 4D.1 follow-up: also refetch whenever the bell sheet
  // transitions from closed to open. Guarantees persistent rows show
  // up without relying on realtime.
  useEffect(() => {
    if (!open) return;
    loadNotifications();
  }, [open, loadNotifications]);
  
  // Subscribe to real-time inserts
  useEffect(() => {
    if (!membership?.shop_id) return;
    
    const channel = supabase
      // R37: Scope channel name with shop id (see useConfirmationAlerts).
      .channel(`notifications-${membership.shop_id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `shop_id=eq.${membership.shop_id}`,
        },
        (payload) => {
          const newNotification: Notification = {
            id: payload.new.id,
            type: payload.new.type,
            title: payload.new.title,
            message: payload.new.message,
            timestamp: new Date(payload.new.created_at),
            read: false,
            data: payload.new.data,
            targetRoles: payload.new.target_roles,
            targetTechId: payload.new.target_tech_id,
          };
          
          notificationContext.addNotification(newNotification);
        }
      )
      .subscribe((status, err) => {
        // Batch 11: normalized log. Intentional CLOSED on
        // membership change / unmount → debug. Real failures
        // (CHANNEL_ERROR, TIMED_OUT, CLOSED-with-err) still
        // warn/error so a silent drop of the toast/badge stream
        // is diagnosable from oncall support tickets.
        logRealtimeStatus('[NotificationPanel]', status, err);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [membership]);
  
  const filteredContextNotifications = userRole 
    ? notificationContext.filterNotificationsByRole(userRole, technicianId || undefined)
    : notificationContext.notifications;
  
  // Merge external and filtered context notifications
  const notifications = [...filteredContextNotifications, ...externalNotifications];
  const unreadCount = notifications.filter(n => !n.read).length;

  // Listen for toast events (for immediate in-memory display)
  useEffect(() => {
    const handleToastNotification = (event: CustomEvent) => {
      const { type, title, message, data } = event.detail;
      notificationContext.addNotification({ type, title, message, data });
    };
    
    window.addEventListener('toast-notification', handleToastNotification as EventListener);
    return () => {
      window.removeEventListener('toast-notification', handleToastNotification as EventListener);
    };
  }, []);
  
  const handleMarkAllAsRead = async () => {
    if (!user || !membership?.shop_id) return;

    try {
      // Get all unread notification IDs from the database
      const unreadNotifications = notifications.filter(n => !n.read);
      const dbNotificationIds = unreadNotifications
        .filter(n => n.id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i))
        .map(n => n.id);

      if (dbNotificationIds.length > 0) {
        // INA-A3: atomic batched RPC replaces the pre-INA-A3 SELECT-then-
        // Promise.all(UPDATE per row) write-side N+1. Single round trip
        // regardless of unread count; idempotent (skips rows already
        // containing user.id in read_by); per-row atomicity via a single
        // UPDATE statement (no lost-update race window).
        await supabase.rpc('notifications_mark_read_for_user', {
          p_notification_ids: dbNotificationIds,
          p_user_id: user.id,
        });
      }

      // Update in memory
      unreadNotifications.forEach(n => {
        notificationContext.markAsRead(n.id);
      });
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleClearAll = async () => {
    if (!user || !membership?.shop_id) return;
    
    try {
      // Get all current notification IDs that are from the database
      const dbNotificationIds = notifications
        .filter(n => n.id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i))
        .map(n => n.id);
      
      if (dbNotificationIds.length > 0) {
        // Actually DELETE the notifications from the database
        const { error: deleteError } = await supabase
          .from('notifications')
          .delete()
          .in('id', dbNotificationIds);
        
        if (deleteError) {
          console.error('Error deleting notifications:', deleteError);
        }
      }
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
    
    // Clear from memory
    notificationContext.clearAll();
    externalClearAll?.();
  };
  
  const handleMarkAsRead = async (id: string) => {
    if (!user) return;

    // Update in DB if it's a DB notification (has UUID format)
    if (id.includes('-')) {
      // INA-A3: atomic RPC replaces pre-A3 SELECT-then-UPDATE shape.
      // Eliminates the lost-update race window between the SELECT and
      // UPDATE (concurrent realtime/insert paths could overwrite a
      // freshly-appended user_id) and collapses 2 round trips → 1.
      await supabase.rpc('notifications_mark_read_for_user', {
        p_notification_ids: [id],
        p_user_id: user.id,
      });
    }

    // Update in memory
    notificationContext.markAsRead(id);
    externalMarkAsRead?.(id);
  };

  // oct20e (FD-2026-10-24-D, CORE-AJ :42): a technician's pause request for the advisor stays open until the advisor
  // addresses or dismisses it. "Addressed" is the advisor's explicit act (server authority, attributed and audited on
  // the request). Removing the request with X is an explicit dismissal: the server keeps the row and its history.
  const isOpenAdvisorRequest = (n: Notification) =>
    (n.type as string) === 'pause_advisor_needed' && !n.data?.resolved_at;

  const handleAddressed = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const { data, error } = await supabase.rpc('resolve_pause_advisor_attention' as never, {
      p_notification_id: id, p_resolution: 'ADDRESSED',
    } as never);
    const outcome = (data as { outcome?: string } | null)?.outcome;
    if (error || (outcome !== 'RESOLVED' && outcome !== 'ALREADY_RESOLVED')) {
      console.error('Error marking the advisor request addressed:', error ?? outcome);
      return;
    }
    await loadNotifications();
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) return;
    
    try {
      // Check if it's a database notification (UUID format)
      const isDbNotification = id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
      
      if (isDbNotification) {
        // Actually DELETE from the database instead of soft delete
        const { error: deleteError } = await supabase
          .from('notifications')
          .delete()
          .eq('id', id);
        
        if (deleteError) {
          console.error('Error deleting notification:', deleteError);
        }
      }
      
      // Always remove from memory immediately for UI feedback
      notificationContext.deleteNotification(id);
    } catch (error) {
      console.error('Error in handleDelete:', error);
      // Still try to remove from memory even if DB update fails
      notificationContext.deleteNotification(id);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'import':
        return '📥';
      case 'reassign':
        return '🔄';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'success':
        return '✅';
      case 'rollover':
        return '🔄';
      case 'timer':
        return '⏱️';
      default:
        return 'ℹ️';
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }

    if (notification.type === 'import' && notification.data && onViewImportReport) {
      onViewImportReport(notification.data);
      setOpen(false);
      return;
    }

    // Slice 4D.1: generic click-through. When `data.target_route` is a
    // safe in-app path, navigate there after marking read. Used by the
    // slice-4D custody notifications (target_route =
    // `/?tab=dayboard&highlight=…&openDetails=true`) but applies to any
    // future notification that opts in by setting this field.
    //
    // Defensive: only navigate when the route is a string starting with
    // a single `/` — rejects protocol-relative (`//example.com`),
    // absolute URLs (`https://…`), `javascript:` schemes, and any other
    // open-redirect shape. The route must also be a plain string; non-
    // string `target_route` values (numbers, objects) are ignored.
    const data = notification.data as { target_route?: unknown } | null | undefined;
    const targetRoute = data?.target_route;
    if (
      typeof targetRoute === 'string' &&
      targetRoute.length > 1 &&
      targetRoute.startsWith('/') &&
      !targetRoute.startsWith('//')
    ) {
      navigate(targetRoute);
      setOpen(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative"
          aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Notifications</SheetTitle>
          <SheetDescription>
            View your recent activity and import reports
          </SheetDescription>
        </SheetHeader>

        {/* OBK-7: durable, role-gated "Needs Confirmation" operator queue —
            persisted via tickets.confirmation_status, so it survives refresh
            and is no longer dependent on the transient 30s toast. */}
        <div className="mt-4">
          <NeedsConfirmationQueue onOpenTicket={() => setOpen(false)} />
        </div>

        <div className="mt-4 flex justify-between items-center">
          <span className="text-sm text-muted-foreground">
            {notifications.length} notifications
          </span>
          {notifications.length > 0 && (
            <div className="flex gap-2">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                >
                  Mark all as read
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
              >
                Clear all
              </Button>
            </div>
          )}
        </div>

        <ScrollArea className="h-[calc(100vh-180px)] mt-4">
          <div className="space-y-2">
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Bell className="h-12 w-12 mx-auto mb-2 opacity-20" />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    notification.read ? 'bg-background' : 'bg-muted/50'
                  } hover:bg-accent`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{getNotificationIcon(notification.type)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{notification.title}</span>
                        {!notification.read && (
                          <div className="h-2 w-2 bg-primary rounded-full" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {notification.timestamp.toLocaleString()}
                      </p>
                      {isOpenAdvisorRequest(notification) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 h-7 text-xs"
                          onClick={(e) => handleAddressed(notification.id, e)}
                        >
                          Addressed
                        </Button>
                      )}
                      {(notification.type as string) === 'pause_advisor_needed' && notification.data?.resolved_at && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {notification.data?.resolved_by === 'DISMISSED' ? 'Dismissed' : 'Addressed'}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDelete(notification.id, e)}
                      className="h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
